/**
 * SGI Quantity Rules - per-product order rules for the quantity counter
 *
 * @ui @component @product @cart @pricing @storefront
 *
 * DEDICATED CUSTOM ASSET (never edit assets/theme.js). Custom element
 * <sgi-quantity-rules> wraps the core <quantity-selector>. It reads
 * data-min / data-step / data-max / data-unit-singular / data-unit-plural from
 * the quantity input (emitted by snippets/sgi-quantity-rules-attrs.liquid) and
 * enforces: start at the minimum, +/- by the increment, clamp to [min, max],
 * round a typed value to the NEAREST allowed quantity (min + k*step <= max;
 * an exact tie rounds UP). An input with no data-min is left completely alone,
 * so products without rules behave exactly as before.
 *
 * Motion: none. Corrections are announced in an aria-live="polite" node.
 */
(function (root) {
  'use strict';

  var EPS = 1e-9;

  function round6(n) { return Math.round(n * 1e6) / 1e6; }

  /** Highest allowed quantity, or Infinity when there is no usable max. */
  function topAllowed(min, step, max) {
    if (!isFinite(max) || max < min) return Infinity;
    return round6(min + Math.floor((max - min) / step + EPS) * step);
  }

  /** Nearest allowed quantity to `value` (tie rounds up); NaN -> min. */
  function nearestAllowed(value, min, step, max) {
    if (!isFinite(value) || value <= min) return min;
    var k = (value - min) / step;
    var lo = Math.floor(k + EPS);
    var q = round6(min + (k - lo >= 0.5 - EPS ? lo + 1 : lo) * step);
    var top = topAllowed(min, step, max);
    return q > top ? top : q;
  }

  function isAllowed(value, min, step, max) {
    return isFinite(value) && nearestAllowed(value, min, step, max) === round6(value);
  }

  var api = { nearestAllowed: nearestAllowed, isAllowed: isAllowed, topAllowed: topAllowed };
  root.SgiQuantityRules = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof HTMLElement === 'undefined' || !root.customElements) return;

  function num(el, key, fallback) {
    var v = parseFloat(el.getAttribute(key));
    return isFinite(v) ? v : fallback;
  }

  var SgiQuantityRules = class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input.quantity-selector__input');
      if (!this.input || !this.input.hasAttribute('data-min')) return;
      this.isCart = this.input.hasAttribute('data-line-key');
      this.note = null;
      this.addEventListener('click', this.onClick.bind(this), true);
      this.addEventListener('change', this.onCommit.bind(this));
      this.addEventListener('focusout', this.onCommit.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      var form = this.input.form;
      if (form) form.addEventListener('submit', this.onCommit.bind(this), true);
      if (!this.isCart) this.setValue(this.rule().min, false);
      this.refresh();
    }

    rule() {
      var i = this.input;
      var min = Math.max(num(i, 'data-min', 1), 1);
      var step = Math.max(num(i, 'data-step', 1), EPS);
      var max = num(i, 'data-max', Infinity);
      var native = parseFloat(i.getAttribute('max'));
      if (isFinite(native) && native >= min) max = Math.min(max, native);
      return { min: min, step: step, max: max };
    }

    plural(n) {
      var s = this.input.getAttribute('data-unit-singular') || '';
      var p = this.input.getAttribute('data-unit-plural') || s;
      return n === 1 ? s : p;
    }

    setValue(v, fire) {
      this.input.value = String(v);
      this.input.style.setProperty('--quantity-selector-character-count', String(v).length + 'ch');
      if (fire !== false) this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    announce(text) {
      if (!this.note) {
        this.note = document.createElement('p');
        this.note.className = 'sgi-quantity-rules__note text-xs text-subdued';
        this.note.setAttribute('role', 'status');
        this.note.setAttribute('aria-live', 'polite');
        var qs = this.querySelector('quantity-selector');
        (qs || this.input).insertAdjacentElement('afterend', this.note);
      }
      this.note.textContent = text || '';
    }

    refresh() {
      var r = this.rule();
      var v = parseFloat(this.input.value);
      var buttons = this.querySelectorAll('button.quantity-selector__button');
      if (!this.isCart && buttons.length > 1) {
        buttons[0].disabled = !(v > r.min);
        buttons[buttons.length - 1].disabled = !(v < topAllowed(r.min, r.step, r.max));
      }
      if (this.isCart) {
        var bad = !isAllowed(v, r.min, r.step, r.max);
        this.dataset.sgiQrInvalid = bad ? '1' : '';
        this.announce(bad ? 'Minimum order is ' + r.min + ' ' + this.plural(r.min) +
          (r.step > 1 ? ', in increments of ' + r.step : '') + '.' : '');
        SgiQuantityRules.syncCheckout();
      }
    }

    onClick(e) {
      var btn = e.target.closest && e.target.closest('button.quantity-selector__button');
      if (!btn || this.isCart || btn.disabled) return;
      e.stopPropagation(); // keep the core stepUp/stepDown and inline +/-1 handlers out
      e.preventDefault();
      var r = this.rule();
      var buttons = this.querySelectorAll('button.quantity-selector__button');
      var dir = btn === buttons[0] ? -1 : 1;
      var cur = nearestAllowed(parseFloat(this.input.value), r.min, r.step, r.max);
      var next = nearestAllowed(cur + dir * r.step, r.min, r.step, r.max);
      this.setValue(next);
      this.refresh();
      if (dir > 0 && next === cur) this.announce('Maximum is ' + cur + ' ' + this.plural(cur) + '.');
    }

    onInput() { this.refresh(); }

    onCommit() {
      var r = this.rule();
      var raw = parseFloat(this.input.value);
      var fixed = nearestAllowed(raw, r.min, r.step, r.max);
      if (fixed === round6(raw)) return this.refresh();
      this.setValue(fixed, !this.isCart);
      this.refresh();
      this.announce('Quantity adjusted to ' + fixed + ' ' + this.plural(fixed) +
        ' (minimum ' + r.min + (r.step > 1 ? ', increments of ' + r.step : '') +
        (isFinite(r.max) ? ', maximum ' + r.max : '') + ').');
    }
  };

  /** Disable checkout while any cart line breaks its rules. */
  SgiQuantityRules.syncCheckout = function () {
    var bad = document.querySelector('sgi-quantity-rules[data-sgi-qr-invalid="1"]');
    document.querySelectorAll('[name="checkout"]').forEach(function (b) {
      if (bad) { b.disabled = true; b.setAttribute('data-sgi-qr-locked', '1'); }
      else if (b.hasAttribute('data-sgi-qr-locked')) { b.disabled = false; b.removeAttribute('data-sgi-qr-locked'); }
    });
  };

  if (!root.customElements.get('sgi-quantity-rules')) {
    root.customElements.define('sgi-quantity-rules', SgiQuantityRules);
  }
})(typeof window !== 'undefined' ? window : globalThis);
